/* ════ AUTH (Supabase Auth) ════════════════════════════════ */
import { createClient } from '@supabase/supabase-js';
import { $ } from './dom.js';
import { state } from './state.js';
import { SB_URL, SB_KEY } from './config.js';
import { sb } from './supabase/client.js';
import { loadAll } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { maybeWhatsNew } from './whatsnew.js';

// A throwaway client for admin "Add user" sign-ups, so creating an account
// never clobbers the admin's own session (signUp would otherwise swap it).
const sbSignup = createClient(SB_URL, SB_KEY, {
  auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
});

// Captured at import time, before supabase-js strips the URL. Invite and
// password-reset links arrive as #...&type=invite|recovery.
const AUTH_HASH = window.location.hash || '';
const AUTH_QUERY = window.location.search || '';
const needsSetPw = () => /type=(invite|recovery)/.test(AUTH_HASH);
// Signed in with Microsoft (a linked "azure" identity)? Then no GRANTT password is needed.
const usesMicrosoft = () => (state.currentUser?.app_metadata?.providers || []).includes('azure')
  || state.currentUser?.app_metadata?.provider === 'azure';
// New users added in-app are flagged so they're prompted to choose their own
// password the first time they sign in (unless they use Microsoft sign-in).
const mustSetPw = () => state.currentUser?.user_metadata?.must_set_password === true && !usesMicrosoft();

let started = false;    // guard so loadAll() only runs once per sign-in
let pwHandled = false;  // once a password is set, don't re-prompt this session

function maybePromptSetPw() {
  if (pwHandled) return;
  if (needsSetPw() || mustSetPw()) openSetPassword(true);
}

const userInitial = u => (u?.email || '?')[0].toUpperCase();

// Make the avatar match the resource linked to this login (same initials/colour).
export function refreshAvatar() {
  const btn = $('btn-logout'); if (!btn) return;
  const email = state.currentUser?.email;
  const res = email && state.resources.find(r => r.user_email === email);
  btn.textContent = res?.ini || userInitial(state.currentUser);
  btn.style.background = res?.col || '';
}

function showApp() {
  if (!started) {
    started = true;
    $('login-screen').classList.add('out');
    setTimeout(() => $('login-screen').style.display = 'none', 520);
    $('app').classList.add('on');
    loadAll();
    maybeWhatsNew();
  }
  refreshAvatar();
  $('user-email').textContent = state.currentUser?.email || '';
}

function showLogin() {
  started = false;
  $('app').classList.remove('on');
  $('login-screen').style.display = 'flex';
  $('login-screen').classList.remove('out');
  $('l-pass').value = '';
}

function shake() {
  const c = document.querySelector('.lg-card');
  c.style.animation = 'none'; c.offsetHeight; c.style.animation = 'shake .4s';
}

async function tryLogin() {
  const email = $('l-user').value.trim(), password = $('l-pass').value;
  if (!email || !password) { $('l-err').textContent = 'Enter your email and password.'; $('l-err').classList.add('show'); return; }
  $('l-btn').disabled = true;
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  $('l-btn').disabled = false;
  if (error || !data?.user) {
    $('l-err').textContent = 'Incorrect email or password — please try again.';
    $('l-err').classList.add('show'); $('l-pass').value = ''; $('l-pass').focus(); shake();
  } else {
    $('l-err').classList.remove('show');
    state.currentUser = data.user; showApp();
  }
}

async function forgotPassword() {
  const email = $('l-user').value.trim();
  if (!email) { $('l-err').textContent = 'Type your email above first, then click Forgot password.'; $('l-err').classList.add('show'); return; }
  const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
  if (error) { $('l-err').textContent = error.message; $('l-err').classList.add('show'); return; }
  $('l-err').classList.remove('show');
  showToast('Password reset link sent — check your email');
}

/* ── Sign in with Microsoft (Entra ID) ───────────────────── */
// The button appears only once the Azure provider is switched on in Supabase,
// so it never leads to an error page. Access is limited to red10's Entra tenant
// by the provider's tenant URL in Supabase.
async function initMicrosoft() {
  const btn = $('l-ms'); if (!btn) return;
  try {
    const r = await fetch(`${SB_URL}/auth/v1/settings`, { headers: { apikey: SB_KEY } });
    const s = await r.json();
    if (s?.external?.azure) $('l-ms-wrap').hidden = false;
  } catch (e) { /* offline or blocked: keep email + password only */ }
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    const { error } = await sb.auth.signInWithOAuth({
      provider: 'azure',
      options: { scopes: 'email', redirectTo: window.location.origin },
    });
    if (error) { btn.disabled = false; showLoginError(error.message); }
  });
  // Coming back from Microsoft with an error (e.g. no GRANTT account yet).
  const p = new URLSearchParams((AUTH_HASH.replace(/^#/, '') || '') + '&' + AUTH_QUERY.replace(/^\?/, ''));
  const desc = p.get('error_description'), code = p.get('error_code') || p.get('error');
  if (desc || code) {
    showLoginError(/signup|not allowed|signups/i.test(`${code} ${desc}`)
      ? 'Your Microsoft account isn’t set up in GRANTT yet. Ask Robin or Vanessa to add you.'
      : `Microsoft sign-in didn’t work: ${desc || code}`);
    history.replaceState(null, '', window.location.pathname);
  }
}
function showLoginError(msg) { $('l-err').textContent = msg; $('l-err').classList.add('show'); }

async function logout() {
  await sb.auth.signOut();
  state.currentUser = null;
  $('user-menu').classList.remove('open');
  showLogin();
  $('l-user').value = '';
}

// Create a login (used from Manage resources). New users are flagged to set
// their own password on first sign-in. Uses the throwaway client so it never
// disturbs the admin's session.
export async function createAuthUser(email, password) {
  return sbSignup.auth.signUp({ email, password, options: { data: { must_set_password: true } } });
}

/* ── Set / change password ───────────────────────────────── */
function openSetPassword(forced) {
  $('user-menu').classList.remove('open');
  $('setpw-title').textContent = forced ? 'Set your password' : 'Change your password';
  $('setpw-msg').textContent = forced
    ? 'Welcome to GRANTT! Choose a password so you can sign in next time.'
    : 'Enter a new password for your account.';
  $('setpw-err').textContent = '';
  $('setpw-pass').value = ''; $('setpw-pass2').value = '';
  $('setpw-cancel').style.display = forced ? 'none' : 'inline-flex';
  $('setpw-x').style.display = forced ? 'none' : 'flex';
  $('setpw-modal').classList.add('open');
  setTimeout(() => $('setpw-pass').focus(), 250);
}
function closeSetPassword() { $('setpw-modal').classList.remove('open'); }

async function saveNewPassword() {
  const p = $('setpw-pass').value, p2 = $('setpw-pass2').value, err = $('setpw-err');
  if (p.length < 6) { err.textContent = 'Password must be at least 6 characters.'; return; }
  if (p !== p2) { err.textContent = 'Passwords do not match.'; return; }
  err.textContent = '';
  $('setpw-btn').disabled = true;
  // Set the password and clear the "must set password" flag in one update.
  const { data, error } = await sb.auth.updateUser({ password: p, data: { must_set_password: false } });
  $('setpw-btn').disabled = false;
  if (error) { err.textContent = error.message; return; }
  if (data?.user) state.currentUser = data.user;
  pwHandled = true;
  $('setpw-pass').value = ''; $('setpw-pass2').value = '';
  closeSetPassword();
  showToast('Password saved ✓');
}

export function initAuth() {
  $('l-btn').addEventListener('click', tryLogin);
  $('l-pass').addEventListener('keydown', e => { if (e.key === 'Enter') tryLogin(); });
  $('l-user').addEventListener('keydown', e => { if (e.key === 'Enter') $('l-pass').focus(); });
  $('l-forgot').addEventListener('click', forgotPassword);
  initMicrosoft();

  // Avatar dropdown (current user, add user, change password, sign out).
  $('btn-logout').addEventListener('click', e => { e.stopPropagation(); $('user-menu').classList.toggle('open'); });
  document.addEventListener('click', e => { if (!e.target.closest('#user-menu') && !e.target.closest('#btn-logout')) $('user-menu').classList.remove('open'); });
  $('btn-signout').addEventListener('click', logout);
  $('btn-changepw').addEventListener('click', () => openSetPassword(false));

  // Set-password modal.
  $('setpw-btn').addEventListener('click', saveNewPassword);
  $('setpw-cancel').addEventListener('click', closeSetPassword);
  $('setpw-x').addEventListener('click', closeSetPassword);
  $('setpw-pass2').addEventListener('keydown', e => { if (e.key === 'Enter') saveNewPassword(); });

  // Restore an existing session, or show the login screen.
  sb.auth.getSession().then(({ data }) => {
    if (data?.session?.user) {
      state.currentUser = data.session.user; showApp(); maybePromptSetPw();
    } else {
      $('login-screen').style.display = 'flex'; setTimeout(() => $('l-user').focus(), 80);
    }
  });

  // React to auth changes (recovery links, sign-out elsewhere, token refresh).
  sb.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') {
      if (session?.user) state.currentUser = session.user;
      showApp(); if (!pwHandled) openSetPassword(true);
    } else if (event === 'SIGNED_OUT') {
      state.currentUser = null; showLogin();
    } else if (session?.user) {
      state.currentUser = session.user; showApp(); maybePromptSetPw();
    }
  });
}
